import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  fetchUserAggregate,
  fetchCustomerAggregate,
  fetchEnrichedEvents,
} from '../analyticsApi';
import type {
  EnrichedEventOut,
  UserAggregateRow,
  CustomerAggregateRow,
} from '../analyticsTypes';
import { readAxiosDetail } from '../../api_calls';
import { useAnalyticsFilters } from '../hooks/useAnalyticsFilters';
import EmptyState from './EmptyState';
import { countDistinctSessions } from '../utils/countDistinctSessions';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
import { opsColors } from '../../../../theme/opsTokens';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip);

export type InspectorTarget =
  | { kind: 'event'; event: EnrichedEventOut }
  | { kind: 'user'; actorId: string; username?: string }
  | { kind: 'customer'; customerId: string; customerName?: string };

interface AnalyticsInspectorProps {
  target: InspectorTarget | null;
  onClose: () => void;
}

const DRAWER_WIDTH = 480;

export default function AnalyticsInspector({ target, onClose }: AnalyticsInspectorProps) {
  const theme = useTheme();
  const open = target !== null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: DRAWER_WIDTH },
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha(opsColors.neutral.panel, 0.96),
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            {target?.kind === 'event' && 'Event detail'}
            {target?.kind === 'user' && 'User detail'}
            {target?.kind === 'customer' && 'Customer detail'}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close inspector">
            <Icon name="xmark" size={20} />
          </IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {target?.kind === 'event' && <EventDetail event={target.event} onClose={onClose} />}
          {target?.kind === 'user' && (
            <UserDetail actorId={target.actorId} username={target.username} onClose={onClose} />
          )}
          {target?.kind === 'customer' && (
            <CustomerDetail
              customerId={target.customerId}
              customerName={target.customerName}
              onClose={onClose}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Event detail
// ──────────────────────────────────────────────────────────────────────────

const EVENT_DETAIL_SAMPLE_SIZE = 200;

interface EventDetailStats {
  totalInWindow: number;
  topUsers: Array<{ name: string; count: number }>;
  topCustomers: Array<{ name: string; count: number }>;
  lastSeenISO: string | null;
}

function EventDetail({ event, onClose }: { event: EnrichedEventOut; onClose: () => void }) {
  const { filters, setFilters } = useAnalyticsFilters();
  const [stats, setStats] = useState<EventDetailStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setStatsLoading(true);
    setStatsError(null);

    fetchEnrichedEvents({
      event_name: event.received_event_name,
      page: 1,
      page_size: EVENT_DETAIL_SAMPLE_SIZE,
      since: filters.since,
      until: filters.until,
    })
      .then((res) => {
        if (controller.signal.aborted) return;
        const userCounts = new Map<string, number>();
        const customerCounts = new Map<string, number>();
        let lastSeen: string | null = null;
        for (const e of res.items) {
          if (e.username) userCounts.set(e.username, (userCounts.get(e.username) ?? 0) + 1);
          if (e.customer_name) customerCounts.set(e.customer_name, (customerCounts.get(e.customer_name) ?? 0) + 1);
          if (!lastSeen || e.event_created_at > lastSeen) lastSeen = e.event_created_at;
        }
        const top = (m: Map<string, number>) => Array.from(m.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, count]) => ({ name, count }));
        setStats({
          totalInWindow: res.total,
          topUsers: top(userCounts),
          topCustomers: top(customerCounts),
          lastSeenISO: lastSeen,
        });
        setStatsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setStatsError(readAxiosDetail(err));
        setStatsLoading(false);
      });

    return () => controller.abort();
  }, [event.received_event_name, filters.since, filters.until]);

  const findSimilar = () => {
    setFilters({ eventName: event.received_event_name, view: 'events' });
    onClose();
  };

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">Event</Typography>
        <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
          {event.received_event_name}
        </Typography>
        {event.catalog_event_name && event.catalog_event_name !== event.received_event_name && (
          <Typography variant="caption" color="text.secondary">
            Catalog: {event.catalog_event_name}
          </Typography>
        )}
      </Stack>

      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        {event.category && <Chip icon={<Icon d={ICON_FALLBACKS.Label} size={20} />} size="small" label={event.category} />}
        {event.screen && <Chip size="small" label={`Screen: ${event.screen}`} variant="outlined" />}
        {event.actor_type && <Chip size="small" label={event.actor_type} variant="outlined" />}
      </Stack>

      {/* Event-level stats */}
      {statsError ? (
        <Alert severity="error" sx={{ py: 0.5 }}>{statsError}</Alert>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1,
          }}
        >
          <StatCell
            icon={<Icon name="calendar-day" size={20} />}
            label="Total in window"
            value={statsLoading ? '…' : (stats?.totalInWindow ?? 0).toLocaleString()}
          />
          <StatCell
            icon={<Icon name="clock" size={20} />}
            label="Last seen"
            value={
              statsLoading
                ? '…'
                : stats?.lastSeenISO
                  ? new Date(stats.lastSeenISO).toLocaleString()
                  : '—'
            }
          />
        </Box>
      )}

      {!statsLoading && stats && (stats.topUsers.length > 0 || stats.topCustomers.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {stats.topUsers.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Top users</Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {stats.topUsers.map((u) => (
                  <Stack key={u.name} direction="row" spacing={1} justifyContent="space-between">
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {u.count}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
          {stats.topCustomers.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Top customers</Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {stats.topCustomers.map((c) => (
                  <Stack key={c.name} direction="row" spacing={1} justifyContent="space-between">
                    <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {c.count}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      <Divider />

      <KvPair label="Time" value={new Date(event.event_created_at).toLocaleString()} />
      <KvPair label="User" value={event.username ?? '—'} />
      <KvPair label="Customer" value={event.customer_name ?? '—'} />
      <KvPair label="Session" value={event.session_id ?? '—'} mono />

      <Divider />

      <Stack spacing={0.75}>
        <Typography variant="caption" color="text.secondary">Payload</Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: 12,
            overflow: 'auto',
            maxHeight: 320,
          }}
        >
          {JSON.stringify(event.meta ?? {}, null, 2)}
        </Box>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" size="small" startIcon={<Icon size={20} name="arrow-up-right-from-square" />} onClick={findSimilar}>
          Find similar events
        </Button>
      </Stack>
    </Stack>
  );
}

function KvPair({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Stack direction="row" spacing={1} sx={{ minHeight: 24 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 84 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: mono ? 'monospace' : undefined, fontSize: mono ? 12 : undefined, wordBreak: 'break-all' }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// User detail
// ──────────────────────────────────────────────────────────────────────────

const SIMULATION_COMPLETE_EVENT = 'simulation_run_complete';
const USER_RECENT_FETCH_SIZE = 200;

function UserDetail({
  actorId,
  username,
  onClose,
}: {
  actorId: string;
  username?: string;
  onClose: () => void;
}) {
  const { filters, setFilters } = useAnalyticsFilters();
  const navigate = useNavigate();
  const [rows, setRows] = useState<UserAggregateRow[]>([]);
  const [userEvents, setUserEvents] = useState<EnrichedEventOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    Promise.all([
      fetchUserAggregate({ actor_id: actorId, page_size: 200, since: filters.since, until: filters.until }),
      // No actor_id filter on /events/enriched yet — fetch a window and client-side
      // filter by username. Yields enough signal for recent activity / last seen.
      username
        ? fetchEnrichedEvents({ page: 1, page_size: USER_RECENT_FETCH_SIZE, since: filters.since, until: filters.until })
        : Promise.resolve({ items: [] as EnrichedEventOut[], total: 0, page: 1, page_size: 0 }),
    ])
      .then(([agg, evs]) => {
        if (controller.signal.aborted) return;
        setRows(agg.items);
        setUserEvents(
          username ? evs.items.filter((e) => e.username === username) : [],
        );
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(readAxiosDetail(err));
        setLoading(false);
      });

    return () => controller.abort();
  }, [actorId, username, filters.since, filters.until]);

  const total = useMemo(() => rows.reduce((s, r) => s + r.count, 0), [rows]);
  const simulationCompletions = useMemo(
    () => rows.filter((r) => r.event_name === SIMULATION_COMPLETE_EVENT).reduce((s, r) => s + r.count, 0),
    [rows],
  );
  const lastSeenISO = userEvents[0]?.event_created_at ?? null;
  const sessionCount = useMemo(() => countDistinctSessions(userEvents), [userEvents]);
  const topScreen = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of userEvents) {
      if (e.screen) counts.set(e.screen, (counts.get(e.screen) ?? 0) + 1);
    }
    let best: string | null = null;
    let bestCount = 0;
    counts.forEach((c, s) => {
      if (c > bestCount) { best = s; bestCount = c; }
    });
    return best;
  }, [userEvents]);

  const viewEvents = () => {
    setFilters({ actorId, view: 'events' });
    onClose();
  };

  const manageUser = () => {
    onClose();
    navigate(`/admin?tab=users&userId=${encodeURIComponent(actorId)}`);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon size={20} name="user" />
        <Stack spacing={0}>
          <Typography variant="h6">{username ?? actorId}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {actorId}
          </Typography>
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1,
        }}
      >
        <StatCell
          icon={<Icon name="calendar-day" size={20} />}
          label="Total events"
          value={total.toLocaleString()}
        />
        <StatCell
          icon={<Icon d={ICON_FALLBACKS.PlayCircleOutline} size={20} />}
          label="Successful sim runs"
          value={simulationCompletions.toLocaleString()}
        />
        <StatCell
          icon={<Icon name="clock-rotate-left" size={20} />}
          label="Sessions seen"
          value={loading ? '…' : sessionCount > 0 ? sessionCount.toLocaleString() : '—'}
          hint={!loading && userEvents.length === 0 && username ? `Based on the last ${USER_RECENT_FETCH_SIZE} events` : undefined}
        />
        <StatCell
          icon={<Icon name="clock" size={20} />}
          label="Last seen"
          value={loading ? '…' : lastSeenISO ? new Date(lastSeenISO).toLocaleString() : '—'}
        />
        {topScreen && (
          <StatCell
            icon={<Icon d={ICON_FALLBACKS.Monitor} size={20} />}
            label="Top screen"
            value={topScreen}
            span={2}
          />
        )}
      </Box>

      <Divider />

      <Typography variant="subtitle2">Top events</Typography>
      <AggregateBarSection rows={rows} loading={loading} error={error} />

      <Divider />

      <Typography variant="subtitle2">Recent activity</Typography>
      <RecentEvents events={userEvents} loading={loading} />
      {!loading && username && userEvents.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No events for this user in the last {USER_RECENT_FETCH_SIZE} recorded events.
        </Typography>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" size="small" startIcon={<Icon size={20} name="arrow-up-right-from-square" />} onClick={viewEvents}>
          View this user&rsquo;s events
        </Button>
        <Button variant="outlined" size="small" startIcon={<Icon size={20} d={ICON_FALLBACKS.ManageAccounts} />} onClick={manageUser}>
          Manage user
        </Button>
      </Stack>
    </Stack>
  );
}

function StatCell({
  icon,
  label,
  value,
  hint,
  span,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
  span?: number;
}) {
  return (
    <Box
      sx={{
        gridColumn: span ? `span ${span}` : undefined,
        px: 1.25,
        py: 1,
        borderRadius: 1,
        backgroundColor: 'action.hover',
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary', mb: 0.25 }}>
        {icon}
        <Typography variant="caption">{label}</Typography>
      </Stack>
      <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.disabled">
          {hint}
        </Typography>
      )}
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Customer detail
// ──────────────────────────────────────────────────────────────────────────

function CustomerDetail({
  customerId,
  customerName,
  onClose,
}: {
  customerId: string;
  customerName?: string;
  onClose: () => void;
}) {
  const { filters, setFilters } = useAnalyticsFilters();
  const [rows, setRows] = useState<CustomerAggregateRow[]>([]);
  const [recent, setRecent] = useState<EnrichedEventOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    Promise.all([
      fetchCustomerAggregate({ customer_id: customerId, page_size: 200, since: filters.since, until: filters.until }),
      fetchEnrichedEvents({ customer_id: customerId, page_size: 25, since: filters.since, until: filters.until }),
    ])
      .then(([agg, evs]) => {
        if (controller.signal.aborted) return;
        setRows(agg.items);
        setRecent(evs.items);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(readAxiosDetail(err));
        setLoading(false);
      });

    return () => controller.abort();
  }, [customerId, filters.since, filters.until]);

  const total = useMemo(() => rows.reduce((s, r) => s + r.count, 0), [rows]);

  const viewEvents = () => {
    setFilters({ customerId, view: 'events' });
    onClose();
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Icon size={20} d={ICON_FALLBACKS.Business} />
        <Stack spacing={0}>
          <Typography variant="h6">{customerName ?? customerId}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {customerId}
          </Typography>
        </Stack>
      </Stack>

      <KvPair label="Total events" value={total.toLocaleString()} />

      <Divider />

      <Typography variant="subtitle2">Top events</Typography>
      <AggregateBarSection rows={rows} loading={loading} error={error} />

      <Divider />

      <Typography variant="subtitle2">Recent activity</Typography>
      <RecentEvents events={recent} loading={loading} />

      <Stack direction="row" spacing={1}>
        <Button variant="contained" size="small" startIcon={<Icon size={20} name="arrow-up-right-from-square" />} onClick={viewEvents}>
          View this customer&rsquo;s events
        </Button>
      </Stack>
    </Stack>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-views
// ──────────────────────────────────────────────────────────────────────────

function AggregateBarSection({
  rows,
  loading,
  error,
}: {
  rows: Array<{ event_name: string; count: number }>;
  loading: boolean;
  error: string | null;
}) {
  const theme = useTheme();
  const sorted = useMemo(() => [...rows].sort((a, b) => b.count - a.count).slice(0, 12), [rows]);

  const data = useMemo(() => ({
    labels: sorted.map((r) => r.event_name),
    datasets: [
      {
        data: sorted.map((r) => r.count),
        backgroundColor: alpha(opsColors.brand.primary, 0.35),
        borderColor: opsColors.brand.primary,
        borderWidth: 1,
        borderRadius: 3,
      },
    ],
  }), [sorted, opsColors.brand.primary]);

  const options = useMemo(() => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: 'index' as const, intersect: false } },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 } },
      y: { ticks: { font: { size: 11 } } },
    },
  }), []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (loading) return <Skeleton variant="rectangular" height={240} />;
  if (sorted.length === 0) return <EmptyState compact title="No events recorded" />;

  return (
    <Box sx={{ height: Math.max(180, sorted.length * 22) }}>
      <Bar data={data} options={options} />
    </Box>
  );
}

function RecentEvents({ events, loading }: { events: EnrichedEventOut[]; loading: boolean }) {
  if (loading) {
    return (
      <Stack spacing={0.75}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (events.length === 0) {
    return <EmptyState compact title="No recent events" />;
  }
  return (
    <Stack spacing={0.5}>
      {events.slice(0, 8).map((e, idx) => (
        <Box
          key={`${e.event_created_at}-${idx}`}
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: 'action.hover',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {new Date(e.event_created_at).toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {e.received_event_name}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
